import React from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { emptyToNull } from "@common/helpers/string";
import {
    CreateCounterpartyContactBody,
    CounterpartyContact,
} from "@common/schemas/counterpartyContact.schema";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { submitHelper } from "@helpers/form";
import {
    StandardModalBody,
    StandardModalFooter,
} from "@design-system/Modal/Modal";
import styles from "./fieldSpacing.module.scss";

interface ContactFormValues {
    label: string;
    title: string;
    firstName: string;
    middleName: string;
    lastName: string;
    suffix: string;
    email: string;
    phoneNumber: string;
}

const fields = createFormikFields<ContactFormValues>();

function toFormValues(contact?: CounterpartyContact): ContactFormValues {
    return {
        label: contact?.label ?? "",
        title: contact?.title ?? "",
        firstName: contact?.firstName ?? "",
        middleName: contact?.middleName ?? "",
        lastName: contact?.lastName ?? "",
        suffix: contact?.suffix ?? "",
        email: contact?.email ?? "",
        phoneNumber: contact?.phoneNumber ?? "",
    };
}

function toContactBody(
    values: ContactFormValues,
): CreateCounterpartyContactBody {
    return {
        label: emptyToNull(values.label),
        title: emptyToNull(values.title),
        firstName: emptyToNull(values.firstName),
        middleName: emptyToNull(values.middleName),
        lastName: emptyToNull(values.lastName),
        suffix: emptyToNull(values.suffix),
        email: emptyToNull(values.email),
        phoneNumber: emptyToNull(values.phoneNumber),
    };
}

interface Props {
    contact?: CounterpartyContact;
    submitText?: string;
    onSubmit: (body: CreateCounterpartyContactBody) => Promise<void>;
    onCancelled: () => void;
}

export const CounterpartyContactForm: React.FC<Props> = ({
    contact,
    submitText = "Save",
    onSubmit,
    onCancelled,
}) => {
    const formik = useFormik<ContactFormValues>({
        initialValues: toFormValues(contact),
        onSubmit: submitHelper({
            handler: (values) => onSubmit(toContactBody(values)),
        }),
    });

    return (
        <FormikProvider value={formik}>
            <Form className={styles.modalForm}>
                <StandardModalBody>
                    <VStack>
                        <fields.Text
                            name="label"
                            label="Label"
                            placeholder="AP, billing, owner…"
                            size="lg"
                            className={styles.inputField}
                        />
                        <fields.Text
                            name="title"
                            label="Title"
                            size="lg"
                            className={styles.inputField}
                        />
                        <fields.Text
                            name="firstName"
                            label="First name"
                            size="lg"
                            className={styles.inputField}
                        />
                        <fields.Text
                            name="middleName"
                            label="Middle name"
                            size="lg"
                            className={styles.inputField}
                        />
                        <fields.Text
                            name="lastName"
                            label="Last name"
                            size="lg"
                            className={styles.inputField}
                        />
                        <fields.Text
                            name="suffix"
                            label="Suffix"
                            size="lg"
                            className={styles.inputField}
                        />
                        <fields.Text
                            name="email"
                            label="Email"
                            size="lg"
                            className={styles.inputField}
                        />
                        <fields.Phone
                            name="phoneNumber"
                            label="Phone number"
                            size="xs"
                            className={styles.inputField}
                        />
                    </VStack>
                </StandardModalBody>
                <StandardModalFooter>
                    <HStack gap={8} justify="end">
                        <Button
                            type="button"
                            variant="secondary"
                            size="md"
                            onClick={onCancelled}
                            disabled={formik.isSubmitting}
                        >
                            Cancel
                        </Button>
                        <ButtonWithLoader
                            type="submit"
                            variant="primary"
                            size="md"
                            loading={formik.isSubmitting}
                        >
                            {submitText}
                        </ButtonWithLoader>
                    </HStack>
                </StandardModalFooter>
            </Form>
        </FormikProvider>
    );
};
